import {BaseModel} from '@/models/base.model.js'
import {messageDirections} from '@/constants/messages.js'

/**
 * Message model
 */
export class Message extends BaseModel {

  //Incoming = Received from user
  get isIncoming() {
    return this.direction === messageDirections.INCOMING
  }

  //Outgoing = Sent by Arist
  get isOutgoing() {
    return this.direction === messageDirections.OUTGOING
  }

  //New messages (won't have ID yet)
  get isNew() {
    return !this.id
  }

  //Helper to set helper flags
  setHelperFlags(lastMessage) {

    //No last message
    if (!lastMessage) {
      this.showDate = true
      this.showTime = true
      this.showCourseName = true
      return
    }

    //Check if course name should be shown
    this.showCourseName = (
      this.courseName && lastMessage.courseName &&
      this.courseName !== lastMessage.courseName
    )

    //Last message has no sent at
    if (!lastMessage.sentAt) {
      this.showDate = true
      this.showTime = true
    }

    //Current message has no sent at
    else if (!this.sentAt) {
      this.showDate = false
      this.showTime = false
    }

    //Check if same day
    else {
      const isSameDay = Number(this.sentAt.startOf('day')) === Number(lastMessage.sentAt.startOf('day'))
      const isSameTime = isSameDay && this.sentAt.toFormat('HH:mm') === lastMessage.sentAt.toFormat('HH:mm')
      const isSameDirection = this.direction === lastMessage.direction

      //Different day
      this.showDate = !isSameDay
      this.showTime = !isSameTime || !isSameDirection
    }
  }

  //Helper to check if the message has any embeddable media
  get hasEmbeddableMedia() {
    if (this.isIncoming) {
      return false
    }
    return (
      this.hasImage ||
      this.hasGif ||
      this.hasUploadedVideo ||
      this.hasExternalVideo
    )
  }

  //Check if we have a YouTube video
  get hasYouTubeVideo() {
    return this.mediaUrl && !!this.youTubeVideoId
  }

  //Get the YouTube video ID from the mediaUrl
  get youTubeVideoId() {

    //Get media URL
    const {mediaUrl} = this

    //For URL's that match the pattern https://www.youtube.com/watch?v=XXXXXXX
    if (mediaUrl.includes('youtube.com/watch?v=')) {
      const url = new URL(mediaUrl)
      return url.searchParams.get('v')
    }

    //For URL's that match the pattern https://www.youtube.com/embed/XXXXXXX
    if (mediaUrl.includes('youtube.com/embed/')) {
      const url = new URL(mediaUrl)
      return url.pathname.split('/')[2]
    }

    //For URL's that match the pattern https://youtu.be/XXXXXXX
    if (mediaUrl.includes('youtu.be/')) {
      const url = new URL(mediaUrl)
      return url.pathname.split('/')[1]
    }

    //Unknown format
    return null
  }

  //Check if we have a Vimeo video
  get hasVimeoVideo() {
    return this.mediaUrl && !!this.vimeoVideoId
  }

  //Get the Vimeo video ID from the mediaUrl
  get vimeoVideoId() {

    //Get media URL
    const {mediaUrl} = this

    //For URL's that match the pattern https://player.vimeo.com/video/XXXXXXX
    if (mediaUrl.includes('player.vimeo.com/video/')) {
      const url = new URL(mediaUrl)
      return url.pathname.split('/')[2]
    }

    //For URL's that match the pattern https://vimeo.com/XXXXXXX
    if (mediaUrl.includes('vimeo.com/')) {
      const url = new URL(mediaUrl)
      return url.pathname.split('/')[1]
    }

    //Unknown format
    return null
  }

  //Check if we have a Loom video
  get hasLoomVideo() {
    return this.mediaUrl && this.mediaUrl.includes('loom.com')
  }

  //Get the Loom video ID from the mediaUrl
  get loomVideoId() {
    const url = new URL(this.mediaUrl)
    return url.pathname.split('/')[2]
  }

  //Check if we have a Microsoft/Sharepoint video
  get hasMicrosoftVideo() {
    return this.mediaUrl && (this.mediaUrl.includes('microsoft.com') || this.mediaUrl.includes('sharepoint.com'))
  }

  //Get message create JSON
  getCreateJson() {
    return {
      body: this.body,
      externalId: this.externalId,
    }
  }
}
